import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useMatches, useParams } from 'react-router';

import { CompanyNav } from '@features/companyNav';
import { Feedback } from '@features/feedbackSidebar';
import { UserNav } from '@features/userNav';

import { selectCompanyUsers, selectIsMini } from '@shared/api/reduxToolkit';
import { ChevronDownIcon, ChevronUpIcon, HamburgerIcon } from '@shared/assets/icons';
import { i18TranslationNS, Translation } from '@shared/config/i18n';
import { useActions } from '@shared/lib/hooks';
import { CollapseMenuButton } from '@shared/ui/collapseMenuButton';

import s from './Sidebar.module.css';

const NAV_COLLAPSIBLE_ID = 'aside-nav';

export const Sidebar = ({ className = '' }) => {
	const { toggleSidebar } = useActions();
	const { companyId: routeCompanyId } = useParams();
	const isMini = useSelector(selectIsMini);
	const companyUsers = useSelector(selectCompanyUsers);
	const { t: tT } = useTranslation(i18TranslationNS);

	// Mobile only: the nav collapses behind a toggle to save vertical space (desktop always shows it,
	// see the display: contents wrapper in the CSS). Collapse again whenever the route changes.
	const [isNavOpen, setIsNavOpen] = useState(false);
	const { pathname } = useLocation();
	useEffect(() => {
		setIsNavOpen(false);
	}, [pathname]);

	const matches = useMatches();
	const currentTitleKey = matches
		.slice()
		.reverse()
		.find((m) => m.handle?.title)?.handle?.title;
	const menuLabel = currentTitleKey ? tT(currentTitleKey) : tT(Translation.SIDEBAR_FULL_ASIDE_LABEL);

	const hasCompanies = companyUsers.length > 0;
	const isCompanyMode = Boolean(routeCompanyId && hasCompanies);

	const ariaLabel = isMini
		? tT(Translation.SIDEBAR_COMPACT_ASIDE_LABEL)
		: tT(Translation.SIDEBAR_FULL_ASIDE_LABEL);

	const classes = {
		aside: clsx(s.sidebar_aside, isMini && s.sidebar_asideMini, className),
		main: clsx('sidebar', isMini && s.sidebar_mainMini),
		collapseButton: s.collapseMenuButton_position,
	};

	const handleToggle = () => {
		toggleSidebar();
	};

	return (
		<aside id='aside' aria-label={ariaLabel} className={classes.aside}>
			<CollapseMenuButton
				className={classes.collapseButton}
				onToggle={handleToggle}
				controlsId='aside'
			/>

			<button
				type='button'
				className={s.sidebar_navToggle}
				onClick={() => setIsNavOpen((open) => !open)}
				aria-expanded={isNavOpen}
				aria-controls={NAV_COLLAPSIBLE_ID}
			>
				<HamburgerIcon className={s.sidebar_navToggle_icon} aria-hidden='true' focusable='false' />
				<span className={clsx('body-medium-base', s.sidebar_navToggle_label)}>{menuLabel}</span>
				{isNavOpen ? (
					<ChevronUpIcon
						className={s.sidebar_navToggle_chevron}
						aria-hidden='true'
						focusable='false'
					/>
				) : (
					<ChevronDownIcon
						className={s.sidebar_navToggle_chevron}
						aria-hidden='true'
						focusable='false'
					/>
				)}
			</button>

			<div
				id={NAV_COLLAPSIBLE_ID}
				className={clsx(s.sidebar_collapsible, isNavOpen && s.sidebar_collapsibleOpen)}
			>
				<div className={classes.main}>
					{!isCompanyMode && <UserNav />}

					{isCompanyMode && <CompanyNav />}
					<Feedback />
				</div>
			</div>
		</aside>
	);
};
